import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import crmService from '../../../services/crmService';

const AssignHRModal = ({ leadIds = [], hrs = [], onClose, onLeadsAssigned }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedHR, setSelectedHR] = useState('');

    const handleAssign = async () => {
        if (!selectedHR) {
            setError('Please select an HR');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Assign leads one by one
            const assignedLeads = [];
            for (const leadId of leadIds) {
                const result = await crmService.assignLeadToHR(leadId, selectedHR);
                if (result?.success) {
                    // You might typically refresh the lead list here, assuming parent handles update
                    // or we fetch the updated lead and return it
                    if (result.data) {
                        assignedLeads.push(result.data);
                    }
                }
            }

            if (assignedLeads.length > 0) {
                onLeadsAssigned?.(assignedLeads);
                onClose();
            } else {
                setError('Failed to assign leads');
            }

        } catch (error) {
            setError('An unexpected error occurred while assigning leads');
            console.log('Lead assignment error:', error);
        } finally {
            setLoading(false);
        }
    };

    const selectedHRName = hrs?.find(h => h.id === selectedHR)?.full_name || '';

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-card rounded-lg shadow-lg w-full max-w-md">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-border">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Icon name="Briefcase" size={20} className="text-primary" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-foreground">Assign HR</h2>
                            <p className="text-sm text-muted-foreground">
                                Assign {leadIds.length} lead{leadIds.length !== 1 ? 's' : ''} to an HR
                            </p>
                        </div>
                    </div>

                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onClose}
                    >
                        <Icon name="X" size={20} />
                    </Button>
                </div>

                {/* Content */}
                <div className="p-6">
                    <div className="space-y-6">
                        {/* Error Message */}
                        {error && (
                            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                                <div className="flex items-center gap-2 text-destructive">
                                    <Icon name="AlertCircle" size={16} />
                                    <p className="font-medium">{error}</p>
                                </div>
                            </div>
                        )}

                        {/* Lead Count Info */}
                        <div className="p-4 bg-muted/50 rounded-lg">
                            <div className="flex items-center gap-2">
                                <Icon name="Users" size={16} className="text-muted-foreground" />
                                <span className="font-medium text-foreground">
                                    {leadIds.length} lead{leadIds.length !== 1 ? 's' : ''} selected for assignment
                                </span>
                            </div>
                        </div>

                        {/* HR Selection */}
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-3">
                                Select HR *
                            </label>

                            {hrs?.length > 0 ? (
                                <Select
                                    value={selectedHR}
                                    onValueChange={setSelectedHR}
                                    placeholder="Choose an HR..."
                                    options={[
                                        { value: '', label: 'Choose an HR...' },
                                        ...hrs.map(hr => ({
                                            value: hr.id,
                                            label: `${hr.full_name} (${hr.email})`
                                        }))
                                    ]}
                                />
                            ) : (
                                <div className="p-4 bg-muted/50 rounded-lg text-center text-sm text-muted-foreground">
                                    No HR users found.
                                </div>
                            )}
                        </div>

                        {/* Assignment Preview */}
                        {selectedHR && selectedHRName && (
                            <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                                <div className="flex items-center gap-2">
                                    <Icon name="ArrowRight" size={16} className="text-primary" />
                                    <span className="text-sm text-foreground">
                                        Assigning to: <strong>{selectedHRName}</strong>
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 pt-6 border-t border-border mt-6">
                        <Button
                            variant="outline"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleAssign}
                            disabled={loading || !selectedHR || hrs?.length === 0}
                            className="flex items-center gap-2"
                        >
                            {loading && (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                            )}
                            Assign HR
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AssignHRModal;
